import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { API } from '../utils/api';

export function ContactModal({ isOpen, onClose, language = 'en' }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', email: '', message: '' });
      setIsSubmitted(false);
      setError('');
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const t = {
    en: {
      title: 'Contact',
      subtitle: 'Have a question or issue? We\'re here to help.',
      name: 'NAME',
      namePlaceholder: 'Your name',
      email: 'EMAIL',
      emailPlaceholder: 'you@example.com',
      message: 'MESSAGE',
      messagePlaceholder: 'How can we help you?',
      submit: 'Send message',
      sending: 'Sending...',
      success: 'Message sent. We\'ll get back to you within 24 hours.',
      error: 'Failed to send message. Please try again.'
    },
    fr: {
      title: 'Contact',
      subtitle: 'Une question ou un problème ? Nous sommes là pour vous aider.',
      name: 'NOM',
      namePlaceholder: 'Votre nom',
      email: 'COURRIEL',
      emailPlaceholder: 'vous@exemple.com',
      message: 'MESSAGE',
      messagePlaceholder: 'Comment pouvons-nous vous aider ?',
      submit: 'Envoyer',
      sending: 'Envoi en cours...',
      success: 'Message envoyé. Nous vous répondrons sous 24 heures.',
      error: 'Échec de l\'envoi. Veuillez réessayer.'
    }
  };

  const text = t[language] || t.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
      } else {
        const data = await response.json();
        setError(data.detail || text.error);
      }
    } catch (err) {
      setError(text.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleBackdropClick}
          data-testid="contact-modal-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-lg bg-white rounded-[20px] shadow-2xl overflow-hidden"
            data-testid="contact-modal"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#F1F5F9] hover:bg-[#E5E7EB] flex items-center justify-center transition-colors z-10"
              type="button"
              aria-label={language === 'fr' ? 'Fermer le formulaire de contact' : 'Close contact form'}
              data-testid="contact-modal-close"
            >
              <X size={20} className="text-[#64748B]" />
            </button>

            {/* Content */}
            <div className="p-6 md:p-8">
              {/* Header */}
              <div className="text-center mb-8 pr-8">
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F172A] mb-2">
                  {text.title}
                </h2>
                <p className="text-[#64748B]">
                  {text.subtitle}
                </p>
              </div>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-8"
                  data-testid="contact-success"
                >
                  <div className="w-16 h-16 bg-[#0F172A]/5 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} weight="fill" className="text-[#0F172A]" />
                  </div>
                  <p className="text-[#0F172A] font-medium text-lg mb-6">
                    {text.success}
                  </p>
                  <button
                    onClick={onClose}
                    className="text-[#64748B] hover:text-[#0F172A] font-medium transition-colors"
                  >
                    {language === 'fr' ? 'Fermer' : 'Close'}
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  {/* Name field */}
                  <div>
                    <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-[0.1em] mb-2">
                      {text.name}
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={text.namePlaceholder}
                      className="w-full px-4 py-3 rounded-[10px] border border-[#E5E7EB] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0F172A]/10 focus:border-[#0F172A]/30 transition-all"
                      data-testid="contact-name-input"
                    />
                  </div>

                  {/* Email field */}
                  <div>
                    <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-[0.1em] mb-2">
                      {text.email}
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={text.emailPlaceholder}
                      className="w-full px-4 py-3 rounded-[10px] border border-[#E5E7EB] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0F172A]/10 focus:border-[#0F172A]/30 transition-all"
                      data-testid="contact-email-input"
                    />
                  </div>

                  {/* Message field */}
                  <div>
                    <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-[0.1em] mb-2">
                      {text.message}
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder={text.messagePlaceholder}
                      className="w-full px-4 py-3 rounded-[10px] border border-[#E5E7EB] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0F172A]/10 focus:border-[#0F172A]/30 transition-all resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  {/* Error message */}
                  {error && (
                    <p className="text-red-500 text-sm" data-testid="contact-error">
                      {error}
                    </p>
                  )}

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#0F172A] hover:bg-[#1E293B] disabled:bg-[#94A3B8] text-white font-medium rounded-[10px] px-6 py-3.5 transition-colors duration-200 flex items-center justify-center gap-2"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? text.sending : text.submit}
                    {!isSubmitting && <ArrowRight size={18} weight="bold" />}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
